import React, { useState } from 'react';
import { FaUser, FaCheckCircle, FaEnvelope } from 'react-icons/fa';
import { createFeedback, getApiErrorMessage } from '../../utils/api';
import StarRating from '../StarRating/StarRating';

const FeedbackForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Please select a rating';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setServerError('');
  };

  const handleRating = (value) => {
    setFormData((prev) => ({ ...prev, rating: value }));

    if (errors.rating) {
      setErrors((prev) => ({ ...prev, rating: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setServerError('');

    try {
      await createFeedback(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        rating: 0,
        message: '',
      });
    } catch (error) {
      setServerError(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FaCheckCircle className="text-4xl text-success" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-gray-800 mb-3">
          Thank You for Your Feedback!
        </h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          Your feedback has been submitted and is pending review. Once approved by our team, it will appear on our Testimonials page.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="btn-primary"
        >
          Submit More Feedback
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-lg p-6 md:p-10"
      noValidate
    >
      <h2 className="text-2xl font-heading font-bold text-gray-800 mb-2">
        Share Your Experience
      </h2>
      <p className="text-gray-500 mb-8">
        We value your feedback. Let us know how we did and help others make informed decisions.
      </p>

      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">
          {serverError}
        </div>
      )}

      <div className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="feedback-name" className="form-label">
            <FaUser className="inline mr-2 text-primary-400" />
            Your Name
          </label>
          <input
            id="feedback-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            className={`form-input ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.name && <p className="form-error">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="feedback-email" className="form-label">
            <FaEnvelope className="inline mr-2 text-primary-400" />
            Your Email
          </label>
          <input
            id="feedback-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            className={`form-input ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.email && <p className="form-error">{errors.email}</p>}
        </div>

        {/* Rating */}
        <div>
          <label className="form-label">Your Rating</label>
          <div className="mt-1">
            <StarRating
              rating={formData.rating}
              size="text-3xl"
              interactive={true}
              onRate={handleRating}
            />
          </div>
          {formData.rating > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              You selected {formData.rating} out of 5 stars
            </p>
          )}
          {errors.rating && <p className="form-error">{errors.rating}</p>}
        </div>

        {/* Message */}
        <div>
          <label htmlFor="feedback-message" className="form-label">
            Your Feedback
          </label>
          <textarea
            id="feedback-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your experience with The Skilled Group..."
            rows="5"
            className={`form-input resize-none ${errors.message ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.message ? (
              <p className="form-error">{errors.message}</p>
            ) : (
              <span />
            )}
            <span className="text-xs text-gray-400">
              {formData.message.length}/1000
            </span>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Your feedback will be reviewed by our team before appearing on the Testimonials page. This usually takes 24-48 hours.
        </p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Submitting...
          </>
        ) : (
          'Submit Feedback'
        )}
      </button>
    </form>
  );
};

export default FeedbackForm;